import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import dayjs from 'dayjs'
import {
  TextField,
  Checkbox,
  Box,
  CardContent,
  Grid,
  CardActions,
  Button,
  FormControlLabel,
  Autocomplete,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate, useParams } from 'react-router-dom'
import { save } from 'store/member/document'
import { BasePathProvider, useBasePath, useMember } from 'components/Providers'
import { DatePicker } from '@mui/x-date-pickers'
import { enqueueSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import Header from 'components/Header'
import { FileUploadField } from 'components/FileUploadField'

const Root = styled(Box)(({ theme }) => ({
  width: theme.breakpoints.values.sm,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}))

const documentTypes = [
  { label: 'Address Proof', value: 'Address Proof' },
  { label: 'Transcript', value: 'Transcript' },
  { label: 'Photograph', value: 'Photograph' },
  { label: 'Diploma', value: 'Diploma' },
  { label: 'Other', value: 'Other' },
];

export default function DocumentForm({ document }) {
  const [state, setState] = useState({
    name: '',
    documentType: '',
    approved: false,
    shared: false,
    expirationDate: null,
    document: '',
  })
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { employmentType, memberId } = useParams()
  const member = useMember()
  const basePath = useBasePath()

  useEffect(() => {
    setState(document)
  }, [document])

  const { t } = useTranslation()

  const dateRuleMax = dayjs(Date.now() + 24 * 60 * 60 * 365 * 1000 * 10)

  const onSubmit = async event => {
    event.preventDefault()

    const data = {
      type: employmentType,
      memberId,
      personId: member?.person?.id,
      ...state,
    }

    delete data.file
    let res
    if (document?.id) {
      res = await dispatch(save({ ...data, id: document.id }))
    } else {
      res = await dispatch(save(data))
    }
    if (res.payload) {
      navigate(basePath)
    }
  }

  return (
    <BasePathProvider value={`${basePath}/documents`}>
      <Root component="form" elevation="0" onSubmit={onSubmit} noValidate>
        <Header title={t(document ? 'Edit Document' : 'Add Document')} small borderBottom close />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item sm={12} xs={12}>
              <Autocomplete
                size="small"
                fullWidth
                options={documentTypes}
                value={
                  documentTypes.find(opt => opt.value === state?.documentType) ||
                  { label: state?.documentType || '', value: state?.documentType || '' }
                }
                onChange={(event, newValue) => {
                  setState(prev => ({
                    ...prev,
                    documentType: newValue?.value ?? '',
                  }));
                }}
                getOptionLabel={option =>
                  typeof option === 'string' ? option : option.label
                }
                renderInput={params => (
                  <TextField {...params} label={t('Document Type')} />
                )}
              />
            </Grid>

            <Grid item sm={12} xs={12}>
              <DatePicker
                label={t('Expiration Date')}
                slotProps={{
                  actionBar: {
                    actions: ['clear'],
                  },
                  field: {
                    fullWidth: true,
                    size: 'small',
                    InputLabelProps: { shrink: true },
                  },
                }}
                fullWidth
                value={(state?.expirationDate && dayjs(state?.expirationDate)) || null}
                onChange={value => setState(prev => ({ ...prev, expirationDate: value }))}
                minDate={dayjs(Date.now())}
                maxDate={dateRuleMax}
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.400',
                  borderRadius: 1,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state?.shared || false}
                      onChange={(event) =>
                        setState((prev) => ({
                          ...prev,
                          shared: event.target.checked,
                        }))
                      }
                    />
                  }
                  label={t('Visible to Parents')}
                />
              </Box>
            </Grid>
            <Grid item sm={12} xs={12}>
              <FileUploadField
                value={state.file || null}
                onChange={async (file) => {
                  if (!file) {
                    setState(prev => ({ ...prev, file: null, fileId: null, name: '' }));
                    return;
                  }

                  const formData = new FormData();
                  formData.append("afile", file);

                  const res = await fetch("/api/file", { method: "POST", body: formData });

                  if (res.status !== 200) {
                    enqueueSnackbar(res.statusText, { variant: "error" });
                    return;
                  }

                  const data = await res.json();

                  setState(prev => ({
                    ...prev,
                    file,
                    fileId: data.id,
                    name: data.name,
                  }));
                }}
                onError={(msg) => msg && enqueueSnackbar(msg, { variant: "error" })}
                label={t('Upload Document')}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', pr: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              navigate(basePath)
            }}
          >
            {t('Cancel')}
          </Button>
          <Button type="submit" variant="contained">
            {t('Submit')}
          </Button>
        </CardActions>
      </Root>
    </BasePathProvider>
  )
}
